import { configureStore, combineReducers, AnyAction } from '@reduxjs/toolkit';
import authReducer, { logoutThunk } from './authSlice';
import { setSessionExpiredHandler } from '../api/client';
import quizzesReducer from './quizzesSlice';
import paywallsReducer from './paywallsSlice';
import projectsReducer, {
  switchActiveProjectThunk,
  clearActiveProjectThunk,
} from './projectsSlice';
import editorReducer from './editorSlice';
// WEB-2138 ч.1: спутник редактора — состояние проверки из ответов сервера.
import editorReviewReducer from './editor/review/reviewSlice';
// WEB-2997: спутник редактора — раскладка графа (места узлов), вне черновика.
import editorGraphReducer from './editor/graph/graphLayoutSlice';
import translationReducer from './translationSlice';
import domainsReducer from './domainsSlice';
import templatesReducer from './templatesSlice';
import emailSequencesReducer from './emailSequencesSlice';
import statisticsReducer from './statisticsSlice';
import projectMembersReducer from './projectMembersSlice';
import projectInvitesReducer from './projectInvitesSlice';
import billingReducer from './billingSlice';
import smartLinksReducer from './smartLinksSlice';
import funnelsReducer from './funnelsSlice';
import organizationsReducer, { switchActiveOrgThunk } from './organizationsSlice';

const appReducer = combineReducers({
  auth: authReducer,
  quizzes: quizzesReducer,
  paywalls: paywallsReducer,
  projects: projectsReducer,
  editor: editorReducer,
  editorReview: editorReviewReducer,
  editorGraph: editorGraphReducer,
  translation: translationReducer,
  domains: domainsReducer,
  templates: templatesReducer,
  emailSequences: emailSequencesReducer,
  statistics: statisticsReducer,
  projectMembers: projectMembersReducer,
  projectInvites: projectInvitesReducer,
  billing: billingReducer,
  smartLinks: smartLinksReducer,
  funnels: funnelsReducer,
  organizations: organizationsReducer,
});

type AppState = ReturnType<typeof appReducer>;

// WEB-1337 P1 (вариант Б развилки — централизованный сброс). These slices
// hold org/project-scoped DATA (остаток, тариф, кошелёк, транзакции,
// строки/итоги статистики, приглашения, домены) but had neither a
// centralized nor a point-wise reset on org/project switch — a stale
// balance/tier/statistics-table/invite-list/domain-list from the PREVIOUS
// org or project could linger on screen after switching until an unrelated
// refetch happened to overwrite it. funnels/quizzes/paywalls are
// DELIBERATELY NOT listed here — they already have their own point-wise
// switchActiveOrgThunk.fulfilled handler (WEB-1058) and now also their own
// switchActiveProjectThunk/clearActiveProjectThunk.fulfilled handler (added
// alongside this atom) — duplicating them into the central list would just
// reset them twice for no benefit.
//
// WEB-1343 п.1/2: projectMembers (members/myRole/myCapabilities) joined this
// list — it held the SAME kind of org/project-scoped data (who am I in this
// project, who's on the team) with NEITHER a centralized NOR a point-wise
// reset, so switching the active project left the previous project's role
// and capabilities driving useProjectRole() (and thus every capability-
// gated button) until one of the three screens that happen to refetch it
// (ProjectSettings/ProjectTeamCard/AiStudio) was visited. The projectId-scoped
// guard in useProjectRole (myCapabilitiesProjectId) already stops a STALE
// bundle from being trusted for a mismatched project; this reset additionally
// clears the visible `members` table immediately instead of leaving it
// showing the previous project's people behind a loading spinner.
const CENTRAL_RESET_INIT_ACTION: AnyAction = { type: '@@WEB_1337_CENTRAL_RESET' };

function resetCentralSlices(state: AppState): AppState {
  return {
    ...state,
    billing: billingReducer(undefined, CENTRAL_RESET_INIT_ACTION),
    statistics: statisticsReducer(undefined, CENTRAL_RESET_INIT_ACTION),
    projectInvites: projectInvitesReducer(undefined, CENTRAL_RESET_INIT_ACTION),
    domains: domainsReducer(undefined, CENTRAL_RESET_INIT_ACTION),
    projectMembers: projectMembersReducer(undefined, CENTRAL_RESET_INIT_ACTION),
  };
}

// On logout (either fulfilled or rejected — logoutThunk clears client storage
// in both cases), wipe ALL slice state so a different user logging in afterwards
// never sees stale data from the previous session. Without this the
// projects/quizzes/etc. slices keep the previous user's data, which both
// (a) flashes that data to the new user briefly and (b) causes the next API
// call to be made with a stale projectId → backend 403 "not a member".
const rootReducer = (state: AppState | undefined, action: AnyAction): AppState => {
  if (
    action.type === logoutThunk.fulfilled.type ||
    action.type === logoutThunk.rejected.type
  ) {
    return appReducer(undefined, action);
  }
  // WEB-1337 P1: reset the central slices' DATA to initial BEFORE the switch
  // action itself is processed — organizationsSlice/projectsSlice still
  // handle the action normally in the same pass (setting activeOrgId/
  // currentProject), they just now start from blanked billing/statistics/
  // projectInvites/domains instead of the previous context's stale values.
  if (
    state &&
    (action.type === switchActiveOrgThunk.fulfilled.type ||
      action.type === switchActiveProjectThunk.fulfilled.type ||
      action.type === clearActiveProjectThunk.fulfilled.type)
  ) {
    return appReducer(resetCentralSlices(state), action);
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
});

// WEB-1114 — the axios response interceptor (client.ts) cannot import this
// store directly (client.ts is a dependency of authSlice via
// tokenStorage/refreshTokenStorage; the reverse import would cycle). It
// exposes a one-shot registration hook instead: when a silent refresh fails
// because the refresh token itself was rejected (expired, or the session was
// revoked — WEB-1091 logout/password-change/reset), it calls this, which
// dispatches the SAME logoutThunk a manual "Log out" click uses — same full
// client-storage cleanup, same rootReducer full-state wipe above, so
// ProtectedRoute redirects to /login exactly as it does for a manual logout.
setSessionExpiredHandler(() => {
  // WEB-1848 — этот выход уже объяснён всплывающим «Сессия истекла — войдите
  // снова» в client.ts. Флаг гасит второе (и неправдивое) сообщение про
  // недоступный сервер: POST /auth/logout отвечает 401 просто потому, что
  // токен стёрт строкой выше.
  store.dispatch(logoutThunk({ sessionExpired: true }));
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
